import { ProductBehavior } from '@up2stay/up2promo-api-client';
import {
  getProductKindLabel,
  intendedBehaviorToProductType,
  type ProductKindLabels,
} from '@up2stay/up2promo-commons/products';
import { Button, Skeleton, cn } from '@up2stay/up2promo-ui';
import {
  BarChart3,
  LogOut,
  MapPinOff,
  Route,
  Star,
  Tag,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { type ReactNode, useMemo } from 'react';

import { formatRelativeTime } from '@/core/components/leads/lead-detail/format-relative-time';
import { SettingsGroupRow } from '@/core/components/settings/settings-group';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useProductEvents } from '@/core/hooks/use-product-events';
import {
  buildHistoryEntries,
  type ProductHistoryActor,
  type ProductHistoryEntry,
  type ProductHistoryIcon,
  type ProductHistoryTarget,
  type ProductHistoryValue,
} from '@/core/lib/product-history';
import { useLocale } from '@/core/providers/dictionary-provider';

/** `products.detail.history` copy (de.json / en.json). */
export type ProductHistoryDict = {
  title?: string;
  info?: string;
  footer?: string;
  claimedEmail?: string;
  claimedEmailPublic?: string;
  claimedManual?: string;
  claimedAdmin?: string;
  claimedSetup?: string;
  claimedStart?: string;
  targetChanged?: string;
  labelChanged?: string;
  feedbackOn?: string;
  feedbackOff?: string;
  stars?: string;
  locationCleared?: string;
  locationClearedUnnamed?: string;
  usageReset?: string;
  released?: string;
  actorSupport?: string;
  actorSystem?: string;
  actorDeleted?: string;
  actorPublic?: string;
  empty?: string;
  emptyHint?: string;
  error?: string;
  errorHint?: string;
  retry?: string;
  showOlder?: string;
  loading?: string;
};

const ICONS: Record<ProductHistoryIcon, LucideIcon> = {
  claimed: UserPlus,
  target: Route,
  label: Tag,
  feedback: Star,
  location: MapPinOff,
  usage: BarChart3,
  released: LogOut,
};

type ProductHistorySectionProps = {
  workspaceId: string | undefined;
  productId: string;
  dict: ProductHistoryDict | undefined;
  kindLabels: ProductKindLabels | undefined;
  /** Workspace locations by id (deleted ones are missing). */
  locationNames: Record<string, string>;
  className?: string;
};

/** Fills `{key}` placeholders: the actor in medium weight, values bold. */
function interpolate(
  template: string,
  actor: string,
  values: Record<string, string>,
): ReactNode {
  return template.split(/(\{[a-zA-Z]+\})/g).map((part, index) => {
    const key = /^\{([a-zA-Z]+)\}$/.exec(part)?.[1];
    if (!key) return <span key={index}>{part}</span>;
    if (key === 'actor') {
      return (
        <span key={index} className="font-medium text-foreground/90">
          {actor}
        </span>
      );
    }
    return (
      <strong key={index} className="font-semibold text-foreground">
        {values[key] ?? '—'}
      </strong>
    );
  });
}

function stripProtocol(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

function formatFullDate(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale === 'de' ? 'de-DE' : 'en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/**
 * "Verlauf" on the product detail (DEV-755): the current workspace's product
 * events, newest first, with "show older entries" loading the next page.
 */
export function ProductHistorySection({
  workspaceId,
  productId,
  dict,
  kindLabels,
  locationNames,
  className,
}: ProductHistorySectionProps) {
  const locale = useLocale();
  const isMobile = useIsMobile();
  const {
    data,
    isLoading,
    isError,
    isFetchNextPageError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProductEvents(workspaceId, productId);

  const entries = useMemo(
    () => buildHistoryEntries(data?.pages.flatMap((page) => page.data) ?? []),
    [data],
  );

  const actorName = (actor: ProductHistoryActor): string => {
    switch (actor.kind) {
      case 'user':
        return actor.name;
      case 'support':
        return dict?.actorSupport ?? 'up2promo support';
      case 'deleted':
        return dict?.actorDeleted ?? 'Deleted user';
      case 'public':
        return dict?.actorPublic ?? 'Someone';
      default:
        return dict?.actorSystem ?? 'up2promo';
    }
  };

  const describeTarget = (target: ProductHistoryTarget | null): string => {
    if (!target) return '—';
    if (target.kind === ProductBehavior.URL && target.url) {
      return stripProtocol(target.url);
    }
    const kindLabel = getProductKindLabel(
      intendedBehaviorToProductType(target.kind),
      kindLabels,
    );
    const locationName = target.locationId
      ? locationNames[target.locationId]
      : undefined;
    return locationName ? `${kindLabel} · ${locationName}` : kindLabel;
  };

  const describe = (value: ProductHistoryValue): string => {
    switch (value.kind) {
      case 'text':
        return value.text;
      case 'target':
        return describeTarget(value.target);
      case 'location':
        return locationNames[value.locationId] ?? '—';
      case 'stars':
        return (dict?.stars ?? '{count} stars').replace(
          '{count}',
          String(value.count),
        );
    }
  };

  const describeAll = (values: Record<string, ProductHistoryValue>) =>
    Object.fromEntries(
      Object.entries(values).map(([key, value]) => [key, describe(value)]),
    );

  const renderEntry = (entry: ProductHistoryEntry, isLast: boolean) => {
    const Icon = ICONS[entry.icon];
    const template = dict?.[entry.template] ?? entry.template;
    const relative = formatRelativeTime(entry.occurredAt, locale);
    const time = (
      <time
        dateTime={entry.occurredAt}
        title={formatFullDate(entry.occurredAt, locale)}
        className={cn(
          'text-muted-foreground/70',
          isMobile && 'mt-0.5 block text-[11.5px]',
        )}
      >
        {isMobile ? relative : ` · ${relative}`}
      </time>
    );
    return (
      <li
        key={entry.id}
        className={cn(
          'relative flex min-w-0 gap-2.5 leading-snug',
          isMobile ? 'text-[13px]' : 'text-xs',
          !isLast && 'pb-4',
        )}
        data-testid="product-history-entry"
      >
        <div className="relative z-[1] flex h-4 w-3.5 shrink-0 items-center justify-center">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        </div>
        {!isLast ? (
          <div
            className="absolute bottom-[4px] left-[7px] top-[calc(1rem+4px)] w-px -translate-x-1/2 bg-border/80"
            aria-hidden
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="min-w-0 break-words text-muted-foreground">
            {interpolate(
              template,
              actorName(entry.actor),
              describeAll(entry.values),
            )}
            {isMobile ? null : time}
          </p>
          {entry.start ? (
            <p className="mt-0.5 min-w-0 break-words text-muted-foreground">
              {interpolate(
                dict?.claimedStart ??
                  'Started with target {target}, name {label}',
                '',
                describeAll(entry.start),
              )}
            </p>
          ) : null}
          {isMobile ? time : null}
        </div>
      </li>
    );
  };

  const loadOlder = () => {
    if (!isFetchingNextPage) void fetchNextPage();
  };
  const errorBlock = (onRetry: () => void) => (
    <div className="flex flex-col items-start gap-2.5" role="alert">
      <div>
        <p className="text-[13.5px] font-semibold text-destructive">
          {dict?.error}
        </p>
        <p className="text-xs text-muted-foreground">{dict?.errorHint}</p>
      </div>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {dict?.retry}
      </Button>
    </div>
  );

  let body: ReactNode;
  if (isLoading) {
    body = (
      <div className="flex flex-col gap-3.5" role="status">
        <span className="sr-only">{dict?.loading}</span>
        {[90, 75, 85].map((width) => (
          <div key={width} className="flex gap-2.5" aria-hidden>
            <Skeleton className="h-3.5 w-3.5 rounded-full" />
            <div className="flex flex-1 flex-col gap-1.5">
              <Skeleton className="h-2.5" style={{ width: `${width}%` }} />
              <Skeleton className="h-2.5 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  } else if (isError && !data) {
    body = errorBlock(() => void refetch());
  } else if (entries.length === 0 && !hasNextPage) {
    body = (
      <div className="py-1 text-center">
        <p className="text-[13.5px] font-semibold text-foreground">
          {dict?.empty}
        </p>
        <p className="text-xs text-muted-foreground">{dict?.emptyHint}</p>
      </div>
    );
  } else {
    // A page can map to fewer rows than events (hidden change fields), so
    // "show older" stays reachable even when the loaded rows are empty.
    body = (
      <>
        {entries.length > 0 ? (
          <ol aria-label={dict?.title}>
            {entries.map((entry, index) =>
              renderEntry(entry, index === entries.length - 1),
            )}
          </ol>
        ) : null}
        {isFetchNextPageError ? (
          <div className={cn(entries.length > 0 && 'mt-3')}>
            {errorBlock(loadOlder)}
          </div>
        ) : hasNextPage ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              'text-muted-foreground hover:text-foreground',
              entries.length > 0 && 'mt-2',
              isMobile ? 'h-11 w-full' : 'h-8 w-auto px-2.5 text-xs',
            )}
            aria-disabled={isFetchingNextPage}
            onClick={loadOlder}
          >
            {isFetchingNextPage ? dict?.loading : dict?.showOlder}
          </Button>
        ) : null}
        <span className="sr-only" aria-live="polite">
          {isFetchingNextPage ? dict?.loading : ''}
        </span>
      </>
    );
  }

  return (
    <SettingsSection
      id="product-history"
      title={dict?.title ?? 'History'}
      info={dict?.info}
      footer={dict?.footer}
      grouped
      className={cn('h-fit', className)}
    >
      <SettingsGroupRow>{body}</SettingsGroupRow>
    </SettingsSection>
  );
}
